import { FC, Fragment } from 'react';
import classnames from 'classnames';
import { format } from 'date-fns';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { Action } from '@hh.ru/magritte-ui-action';
import { DateType, Calendar, BaseDatePickerProps } from '@hh.ru/magritte-ui-date-picker/types';
import { makeYearsInterval } from '@hh.ru/magritte-ui-date-picker/utils';
import {
    ChevronLeftOutlinedSize24,
    ChevronRightOutlinedSize24,
    ChevronDownOutlinedSize16,
} from '@hh.ru/magritte-ui-icon/icon';

import styles from './date-picker.less';

export const Header: FC<
    Pick<
        BaseDatePickerProps,
        'activeHeader' | 'alignHeader' | 'headerMonthFormat' | 'locale' | 'showControls' | 'showHeaderDate'
    > & {
        calendar: Calendar;
        displayDate: DateType;
        goToNextPeriod?: VoidFunction;
        goToPrevPeriod?: VoidFunction;
        showNextControl?: boolean;
        showPrevControl?: boolean;
        switchCalendar?: VoidFunction;
    }
> = ({
    calendar,
    displayDate,
    activeHeader: _activeHeader = true,
    alignHeader = 'center',
    goToNextPeriod,
    goToPrevPeriod,
    headerMonthFormat = 'mm',
    locale,
    showControls = true,
    showHeaderDate = true,
    showNextControl = true,
    showPrevControl = true,
    switchCalendar,
}) => {
    const forceDisabled = useDisabled();
    const activeHeader = _activeHeader && !forceDisabled;

    if (!showControls && !showHeaderDate) {
        return null;
    }

    return (
        <div className={styles.headerWrapper}>
            <div className={styles.header} data-qa="date-picker-header">
                {showControls && (
                    <div className={styles.control}>
                        {showPrevControl && goToPrevPeriod && (
                            <Action
                                icon={ChevronLeftOutlinedSize24}
                                mode="secondary"
                                style="neutral"
                                onClick={goToPrevPeriod}
                                data-qa="date-picker-prev-control"
                            />
                        )}
                    </div>
                )}
                <div
                    className={classnames({
                        [styles.headerDateWrapperCenter]: alignHeader === 'center',
                        [styles.headerDateWrapperLeft]: alignHeader === 'left',
                    })}
                >
                    {showHeaderDate && (
                        <button
                            className={classnames(styles.headerDate, { [styles.headerDateActive]: activeHeader })}
                            data-qa="date-picker-header-date"
                            disabled={!activeHeader}
                            onClick={switchCalendar}
                        >
                            {calendar === 'day' &&
                                format(displayDate, headerMonthFormat.endsWith('yyyy') ? 'LLLL yyyy' : 'LLLL', {
                                    locale,
                                })}
                            {calendar === 'month' && format(displayDate, 'yyyy', { locale })}
                            {calendar === 'year' && (
                                <Fragment>
                                    {format(makeYearsInterval(displayDate).start, 'yyyy', { locale })}
                                    {` – `}
                                    {format(makeYearsInterval(displayDate).end, 'yyyy', { locale })}
                                </Fragment>
                            )}
                            {activeHeader && <ChevronDownOutlinedSize16 initial="secondary" mode="neutral" />}
                        </button>
                    )}
                </div>
                {showControls && (
                    <div className={styles.control}>
                        {showNextControl && goToNextPeriod && (
                            <Action
                                icon={ChevronRightOutlinedSize24}
                                mode="secondary"
                                style="neutral"
                                onClick={goToNextPeriod}
                                data-qa="date-picker-next-control"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
